/**
 * @file report.service.ts
 * @description App-level facade + view-model orchestration for reports.
 *
 * Responsibilities split with the shared adapter
 * (`@kirigami/data-access/report-adapters-web`):
 *
 *   • Transport / HTTP                  → `ReportWebService`  (adapter)
 *   • Section-ID normalization,         → this file
 *     UID / stage / priority enrichment,
 *     computed scores, query helpers,
 *     paper-title extraction
 *
 * The facade exposes the same surface that components have always used,
 * so dashboard, summary, and detail pages keep working unchanged.
 *
 * @see libs/data-access/report-adapters-web/src/lib/report-web.service.ts
 */

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, last, map, switchMap, tap } from 'rxjs';
import {
  Report,
  Question,
  Section,
  Relation,
  FeedbackItem,
  getStage,
  getPriority,
} from '../models/report.model';
import { ReportWebService } from '@kirigami/data-access/report-adapters-web';
import type {
  CheckCreditsResponse,
  ConsumptionHistoryEntry,
  RegistryReportDto,
  ReportSectionContentDto,
  ReportUserDto,
  ReportWithFilesDto,
} from '@kirigami/data-access/registry-api-contracts';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  /** Observable stream of the currently loaded report. Null if no report loaded yet. */
  private report$ = new BehaviorSubject<Report | null>(null);

  /** Auto-incrementing counter for generating unique IDs (fb-0, fb-1, rel-0, rel-1, etc.) */
  private uidCounter = 0;

  readonly #adapter = inject(ReportWebService);
  readonly #analytics = inject(AnalyticsService);

  // ── Data Loading ──────────────────────────────────

  /**
   * Loads and processes a report from the backend API.
   *
   * Delegates the transport (report-with-files + presigned analysis JSON
   * resolution) to `ReportWebService.loadReportWithAnalysis`, then runs
   * the app-local enrichment pipeline to produce a `Report` view-model.
   * Threads `owner` (and `title`) from the report record into the result
   * because the analysis JSON does not carry them — owner-only gating
   * in the report-detail surface depends on it.
   */
  loadReport(reportId: string): Observable<Report> {
    return this.#adapter.loadReportWithAnalysis(reportId).pipe(
      map(({ report: record, analysis }) => {
        const report = this.processReport(analysis);
        if (record?.owner) report.owner = record.owner;
        if (record?.title && !report.paper_title) report.paper_title = record.title;
        return report;
      }),
    );
  }

  // ── Report CRUD (API) ────────────────────────────────

  /** Fetches the authenticated user's report list from the backend. */
  fetchReports(): Observable<RegistryReportDto[]> {
    return this.#adapter.fetchReports();
  }

  /** Single-shot fetch of `{ report, files }` for the given id. Distinct
   *  from `pollReportStatus`: this one snapshot is what
   *  `ReportPollingService` calls inside its own backoff loop. */
  fetchReportWithFiles(reportId: string): Observable<ReportWithFilesDto> {
    return this.#adapter.fetchReportWithFiles(reportId);
  }

  /**
   * Creates a new empty report. `status` / `processing_status` defaults
   * match `web-console`'s `new_empty_report()` — backend rejects the
   * call with 422 if either is missing.
   */
  createReport(
    title: string,
    description: string,
    filename: string,
    owner: string,
    subscriptionId: string,
  ): Observable<RegistryReportDto> {
    return this.#adapter.createReport({
      title,
      description,
      owner,
      subscription_id: subscriptionId,
      input_files: [filename],
      status: 'DRAFT',
      processing_status: 'PENDING',
    });
  }

  /**
   * Renames a report. The backend updates the report row and the
   * denormalized title on every user's access row in one call.
   */
  updateReportTitle(reportId: string, title: string): Observable<unknown> {
    return this.#adapter.updateReport(reportId, { title });
  }

  /** Deletes a report by ID. */
  deleteReport(reportId: string): Observable<void> {
    return this.#adapter.deleteReport(reportId);
  }

  // ── Report Sharing ────────────────────────────────

  /** Invites a user to access a report by email. */
  addUserToReport(reportId: string, email: string, role = 'VIEWER'): Observable<ReportUserDto> {
    return this.#adapter.addUserToReport(reportId, { email, role });
  }

  /** Removes a user's access to a report. */
  removeUserFromReport(reportId: string, userPk: string): Observable<void> {
    return this.#adapter.removeUserFromReport(reportId, userPk);
  }

  /** Fetches the list of users who have access to a report. */
  fetchReportUsers(reportId: string): Observable<ReportUserDto[]> {
    return this.#adapter.fetchReportUsers(reportId);
  }

  /** Resolves a presigned download URL for a report's first original input file. */
  downloadOriginalFile(reportId: string): Observable<string> {
    return this.#adapter.downloadOriginalFile(reportId);
  }

  /** Pre-analysis text-extractability gate — returns the extracted text. */
  extractText(filename: string): Observable<string> {
    return this.#adapter.extractText(filename);
  }

  /** Advisory credit pre-flight. Backend `start-analysis` is the authoritative gate. */
  checkCredits(subscriptionId: string, reportType = 'standard'): Observable<CheckCreditsResponse> {
    return this.#adapter.checkCredits(subscriptionId, reportType);
  }

  /** Fetches the user's credit-ledger movements (reserve / debit / release), newest-first. */
  getConsumptionHistory(): Observable<ConsumptionHistoryEntry[]> {
    return this.#adapter.getConsumptionHistory();
  }

  /**
   * Pushes edited section content into the backend cache before a
   * selective re-analysis. `sectionId` must be the canonical normalized
   * form (use {@link normalizeSectionId}); the display label is wrong.
   */
  updateSectionsContentCache(
    reportId: string,
    sections: ReportSectionContentDto[],
  ): Observable<unknown> {
    return this.#adapter.updateSectionsContentCache(reportId, sections);
  }

  /** Triggers the browser-shell analysis run for a report. */
  startAnalysis(reportId: string, subscriptionId: string): Observable<unknown> {
    return this.#adapter
      .startAnalysis({
        report_id: reportId,
        subscription_id: subscriptionId,
      })
      .pipe(
        tap(() =>
          this.#analytics.track({ event: 'analysis_start', mode: 'initial', report_id: reportId }),
        ),
      );
  }

  /**
   * Triggers a selective re-analysis scoped to the given canonical
   * section ids. Mirrors the add-in's `nodes_to_run = [...]` semantics
   * (KB §8.3 "Analysis flow"). The backend expects every entry in
   * `nodes_to_run` to be lowercase — the canonical normalised form —
   * so we coerce here as a guard for any caller that passes display
   * labels by mistake.
   *
   * **Subscription rule:** the run is billed against the caller's
   * active subscription (`subscriptionId`), consistent with the initial
   * and full-document analysis paths. The token settlement on the
   * engine callback debits this subscription's wallet.
   */
  startSectionAnalysis(
    reportId: string,
    subscriptionId: string,
    sectionIds: string[],
  ): Observable<unknown> {
    return this.#adapter
      .startAnalysis({
        report_id: reportId,
        subscription_id: subscriptionId,
        nodes_to_run: sectionIds.map((s) => s.toLowerCase()),
      })
      .pipe(
        tap(() =>
          this.#analytics.track({ event: 'analysis_start', mode: 'sections', report_id: reportId }),
        ),
      );
  }

  /**
   * Triggers a full-document re-analysis. Sends `nodes_to_run: []`,
   * which the backend treats as "restore-from-cache": rerun every
   * section against the latest cached content. The cache update step
   * runs first (via {@link runFullAnalysis}), so this re-analyses
   * with the user's drafted edits applied across every section.
   */
  startFullAnalysis(reportId: string, subscriptionId: string): Observable<unknown> {
    return this.#adapter
      .startAnalysis({
        report_id: reportId,
        subscription_id: subscriptionId,
        nodes_to_run: [],
      })
      .pipe(
        tap(() =>
          this.#analytics.track({ event: 'analysis_start', mode: 'full', report_id: reportId }),
        ),
      );
  }

  /**
   * Per-section re-analysis pipeline:
   *   1. PUT updated section content to the cache
   *   2. POST start-analysis with `nodes_to_run` = drafted section ids
   *   3. Poll until the report's processing_status leaves PENDING/PROCESSING
   *   4. Reload + emit the freshly enriched Report
   *
   * Errors at any step propagate; drafts are not modified by this method
   * — the caller is responsible for clearing them on success.
   */
  runSectionAnalysis(
    reportId: string,
    subscriptionId: string,
    drafts: ReportSectionContentDto[],
  ): Observable<Report> {
    const normalised = drafts.map(toLowerCaseDraft);
    const sectionIds = normalised.map((d) => d.section_name);
    return this.updateSectionsContentCache(reportId, normalised).pipe(
      switchMap(() => this.startSectionAnalysis(reportId, subscriptionId, sectionIds)),
      switchMap(() => this.pollReportStatus(reportId).pipe(last())),
      switchMap(() => this.loadReport(reportId)),
    );
  }

  /**
   * Full-document re-analysis pipeline. Same shape as
   * {@link runSectionAnalysis} but issues a full-pipeline analysis.
   * Drafts are still pushed to the cache so the new analysis sees the
   * latest text — see KB §8.3.
   */
  runFullAnalysis(
    reportId: string,
    subscriptionId: string,
    drafts: ReportSectionContentDto[],
  ): Observable<Report> {
    const normalised = drafts.map(toLowerCaseDraft);
    return this.updateSectionsContentCache(reportId, normalised).pipe(
      switchMap(() => this.startFullAnalysis(reportId, subscriptionId)),
      switchMap(() => this.pollReportStatus(reportId).pipe(last())),
      switchMap(() => this.loadReport(reportId)),
    );
  }

  /** Polls the report status at a fixed interval until processing is terminal. */
  pollReportStatus(reportId: string, intervalMs = 15000): Observable<ReportWithFilesDto> {
    return this.#adapter.pollReportStatus(reportId, intervalMs);
  }

  /**
   * Extracts the paper title from a report using a resilient fallback chain:
   * 1. `report.paper_title` — explicit field from backend (not yet wired)
   * 2. Section with `section_type === 'title'`
   * 3. Section with `section_name === 'title'` (case-insensitive)
   * 4. First section's content (usually the title in scientific papers)
   * 5. `report.report_id` (UUID)
   * 6. Provided fallback string
   */
  extractPaperTitle(report: Report, fallback = 'Untitled Report'): string {
    const byField = report.paper_title;
    const byType = report.sections.find((s) => s.section_type?.toLowerCase() === 'title');
    const byName = report.sections.find((s) => s.section_name.toLowerCase() === 'title');
    const firstSection = report.sections[0];
    return (
      byField?.trim() ||
      byType?.section_content?.trim() ||
      byName?.section_content?.trim() ||
      firstSection?.section_content?.trim() ||
      report.report_id ||
      fallback
    );
  }

  /** Returns an observable stream of the current report (or null). */
  getReport(): Observable<Report | null> {
    return this.report$.asObservable();
  }

  /** Updates the current report in the BehaviorSubject. Call after loading or modifying. */
  setReport(report: Report): void {
    this.report$.next(report);
  }

  // ── Section ID Normalization ──────────────────────

  /**
   * Lookup map from lowercase/snake_case section IDs to canonical section names.
   * Built once per report load via buildSectionLookup().
   */
  private sectionLookup = new Map<string, string>();

  /** Builds the normalization lookup from all section names in the report. */
  private buildSectionLookup(sections: Section[]): void {
    this.sectionLookup.clear();
    for (const s of sections) {
      const name = s.section_name;
      this.sectionLookup.set(name.toLowerCase(), name);
      const snakeForm = name.toLowerCase().replace(/\s+/g, '_');
      this.sectionLookup.set(snakeForm, name);
    }
  }

  /** Converts any section identifier format to the canonical display name. */
  normalizeSectionId(id: string): string {
    return (
      this.sectionLookup.get(id.toLowerCase()) ||
      this.sectionLookup.get(id.toLowerCase().replace(/\s+/g, '_')) ||
      id
    );
  }

  // ── Report Processing Pipeline ────────────────────

  /** Transforms raw analysis JSON into a fully typed and enriched Report view-model. */
  processReport(data: unknown): Report {
    const raw = (data || {}) as {
      sections?: unknown[];
      questions?: unknown[];
      relations?: unknown[];
      stage?: string;
      report_id?: string;
      status?: string;
    };

    const sections = this.processSections(raw.sections || []);
    this.buildSectionLookup(sections);

    const report: Report = {
      stage: raw.stage ?? '',
      report_id: raw.report_id ?? '',
      status: raw.status ?? '',
      sections,
      questions: this.processQuestions(raw.questions || []),
      relations: this.processRelations(raw.relations || []),
    };

    report.contentScore = this.computeAverageScore(report.questions.flatMap((q) => q.feedback));
    report.sectionScore = this.computeAverageScore(
      report.sections.flatMap((s) => s.feedback || []),
    );
    report.coherenceScore = this.computeAverageScore(report.relations);
    report.overallScore =
      Math.round(((report.contentScore + report.sectionScore + report.coherenceScore) / 3) * 10) /
      10;

    return report;
  }

  /** Processes raw sections, enriching each feedback item with sectionName for later filtering. */
  private processSections(sections: unknown[]): Section[] {
    const processed = sections.map((raw) => {
      const s = raw as Section & { feedback?: FeedbackItem[] };
      return {
        ...s,
        feedback: (s.feedback || []).map((fb) => ({
          ...this.enrichFeedback(fb),
          sectionName: s.section_name,
        })),
      };
    });

    // "Other" sections always render last in the report detail, regardless of
    // the order returned by the backend.
    const isOther = (s: Section) => s.section_name?.trim().toLowerCase() === 'other';
    return [...processed.filter((s) => !isOther(s)), ...processed.filter(isOther)];
  }

  /** Processes raw questions, enriching each feedback item with UID, stage, and priority. */
  private processQuestions(questions: unknown[]): Question[] {
    return questions.map((raw) => {
      const q = raw as Question;
      return {
        ...q,
        feedback: (q.feedback || []).map((fb) => this.enrichFeedback(fb)),
      };
    });
  }

  /** Processes raw relations, adding UID, stage, priority, and initial status. */
  private processRelations(relations: unknown[]): Relation[] {
    return relations.map((raw) => {
      const r = raw as Relation;
      return {
        ...r,
        _uid: `rel-${this.uidCounter++}`,
        stage: getStage(r.score),
        priority: getPriority(r.score),
        status: 'pending' as const,
      };
    });
  }

  /** Enriches a raw feedback item with derived fields (_uid, stage, priority, status). */
  private enrichFeedback(fb: FeedbackItem): FeedbackItem {
    return {
      ...fb,
      _uid: `fb-${this.uidCounter++}`,
      stage: getStage(fb.score),
      priority: getPriority(fb.score),
      status: 'pending' as const,
    };
  }

  /** Computes the average score from an array of scored items. Result rounded to 1 decimal.
   *  Items with a missing `score` count as 0 — backend payloads occasionally omit it. */
  private computeAverageScore(items: { score?: number }[]): number {
    if (!items.length) return 0;
    const sum = items.reduce((acc, item) => acc + (item.score ?? 0), 0);
    return Math.round((sum / items.length) * 10) / 10;
  }

  // ── Query Methods ─────────────────────────────────

  /** Returns all content analysis questions targeting a specific section. */
  getQuestionsForSection(report: Report, sectionId: string): Question[] {
    const canonical = this.normalizeSectionId(sectionId);
    return report.questions.filter((q) => this.normalizeSectionId(q.sectionId) === canonical);
  }

  /** Returns the structure feedback items for a specific section. */
  getSectionFeedback(report: Report, sectionName: string): FeedbackItem[] {
    const canonical = this.normalizeSectionId(sectionName);
    const section = report.sections.find(
      (s) => this.normalizeSectionId(s.section_name) === canonical,
    );
    return section?.feedback || [];
  }

  /** Returns all coherence relations involving a specific section (as source OR target). */
  getRelationsForSection(report: Report, sectionName: string): Relation[] {
    const canonical = this.normalizeSectionId(sectionName);
    return report.relations.filter(
      (r) =>
        this.normalizeSectionId(r.section_from) === canonical ||
        this.normalizeSectionId(r.section_to) === canonical,
    );
  }

  /** Returns all section names in the order they appear in the report. */
  getSectionNames(report: Report): string[] {
    return report.sections.map((s) => s.section_name);
  }

  /** Computes the aggregate score for a section across all three dimensions. */
  getSectionScore(report: Report, sectionName: string): number {
    const canonical = this.normalizeSectionId(sectionName);
    const contentFb = report.questions
      .filter((q) => this.normalizeSectionId(q.sectionId) === canonical)
      .flatMap((q) => q.feedback);
    const sectionFb = this.getSectionFeedback(report, sectionName);
    const relationFb = this.getRelationsForSection(report, sectionName);

    const allScores = [
      ...contentFb.map((f) => f.score ?? 0),
      ...sectionFb.map((f) => f.score ?? 0),
      ...relationFb.map((r) => r.score ?? 0),
    ];

    if (!allScores.length) return 0;
    return Math.round((allScores.reduce((a, b) => a + b, 0) / allScores.length) * 10) / 10;
  }

  /** Counts the total number of feedback issues for a section across all dimensions. */
  getSectionIssueCount(report: Report, sectionName: string): number {
    const canonical = this.normalizeSectionId(sectionName);
    const contentCount = report.questions
      .filter((q) => this.normalizeSectionId(q.sectionId) === canonical)
      .reduce((acc, q) => acc + q.feedback.length, 0);
    const sectionCount = this.getSectionFeedback(report, sectionName).length;
    const relationCount = this.getRelationsForSection(report, sectionName).filter(
      (r) => r.problem !== 'None',
    ).length;
    return contentCount + sectionCount + relationCount;
  }

  /** Returns the N lowest-scoring feedback items across all three dimensions. */
  getTopPriorities(
    report: Report,
    limit = 5,
  ): {
    type: 'content' | 'section' | 'coherence';
    label: string;
    section: string;
    feedback: FeedbackItem | Relation;
  }[] {
    const items: {
      type: 'content' | 'section' | 'coherence';
      label: string;
      section: string;
      feedback: FeedbackItem | Relation;
      score: number;
    }[] = [];

    report.questions.forEach((q) => {
      q.feedback.forEach((fb) => {
        items.push({
          type: 'content',
          label: q.question,
          section: q.sectionId,
          feedback: fb,
          score: fb.score,
        });
      });
    });

    report.sections.forEach((s) => {
      (s.feedback || []).forEach((fb) => {
        items.push({
          type: 'section',
          label: fb.category,
          section: s.section_name,
          feedback: fb,
          score: fb.score,
        });
      });
    });

    report.relations.forEach((r) => {
      if (r.problem !== 'None') {
        items.push({
          type: 'coherence',
          label: `${r.section_from} ↔ ${r.section_to}`,
          section: `${r.section_from} & ${r.section_to}`,
          feedback: r,
          score: r.score,
        });
      }
    });

    return items.sort((a, b) => a.score - b.score).slice(0, limit);
  }
}

/**
 * Coerces a draft's `section_name` to lowercase. The registry backend
 * expects the canonical normalised form on both
 * `update-sections-content-cache` and `start-analysis.nodes_to_run`;
 * callers that pass the display label ("Abstract") would otherwise be
 * silently ignored.
 */
function toLowerCaseDraft(draft: ReportSectionContentDto): ReportSectionContentDto {
  return { section_name: draft.section_name.toLowerCase(), section_content: draft.section_content };
}
